import { useState } from 'react';
import { format, subDays } from 'date-fns';

import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { usePaymentSpeedKPI, type SpeedStats } from '@/hooks/finance/usePaymentSpeedKPI';

const formatDuration = (hours: number | null) => {
  if (hours === null) return '—';
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const formatEtb = (value: number) => {
  if (value >= 1_000_000) return `ETB ${(value / 1_000_000).toFixed(2)}m`;
  if (value >= 1_000) return `ETB ${(value / 1_000).toFixed(1)}k`;
  return `ETB ${value.toFixed(0)}`;
};

const deltaText = (current: number | null, previous: number | null) => {
  if (current === null || previous === null || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  const faster = pct < 0;
  return {
    faster,
    label: `${Math.abs(pct).toFixed(1)}% ${faster ? 'faster' : 'slower'} vs previous period`,
  };
};

type KpiTone = 'good' | 'bad' | 'neutral';

const toneClasses: Record<KpiTone, { bar: string; bg: string; value: string }> = {
  good: { bar: 'bg-emerald-600', bg: 'bg-emerald-500/10', value: 'text-emerald-700 dark:text-emerald-400' },
  bad: { bar: 'bg-destructive', bg: 'bg-destructive/10', value: 'text-destructive' },
  neutral: { bar: 'bg-amber-500', bg: 'bg-amber-500/10', value: 'text-amber-600 dark:text-amber-400' },
};

const KpiCard = ({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: KpiTone;
}) => {
  const t = toneClasses[tone];
  return (
    <div className="flex overflow-hidden rounded-xl">
      <div className={`w-1.5 shrink-0 ${t.bar}`} />
      <div className={`flex-1 rounded-r-xl px-5 py-4 ${t.bg}`}>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`mt-2 text-3xl font-semibold ${t.value}`}>{value}</p>
        {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
};

const rowTone = (current: number | null, previous: number | null): string => {
  if (current === null || previous === null) return 'text-foreground';
  return current <= previous ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive';
};

export default function PaymentSpeedKPIPage() {
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 6), 'yyyy-MM-dd'));

  const { data, isLoading } = usePaymentSpeedKPI(dateFrom, dateTo);

  const current: SpeedStats | undefined = data?.current;
  const previous: SpeedStats | undefined = data?.previous;

  const rows: { label: string; current: number | null; previous: number | null; kind: 'duration' | 'money' | 'count' }[] =
    [
      {
        label: 'Request → approval',
        current: current?.requestToApprovalHours ?? null,
        previous: previous?.requestToApprovalHours ?? null,
        kind: 'duration',
      },
      {
        label: 'Approval → paid',
        current: current?.approvalToPaidHours ?? null,
        previous: previous?.approvalToPaidHours ?? null,
        kind: 'duration',
      },
      {
        label: 'Request → paid average',
        current: current?.requestToPaidAvgHours ?? null,
        previous: previous?.requestToPaidAvgHours ?? null,
        kind: 'duration',
      },
      {
        label: 'Request → paid median',
        current: current?.requestToPaidMedianHours ?? null,
        previous: previous?.requestToPaidMedianHours ?? null,
        kind: 'duration',
      },
      {
        label: 'ETB paid',
        current: current?.etbPaid ?? null,
        previous: previous?.etbPaid ?? null,
        kind: 'money',
      },
      {
        label: 'Paid payments (count)',
        current: current?.paidCount ?? null,
        previous: previous?.paidCount ?? null,
        kind: 'count',
      },
    ];

  const renderCell = (value: number | null, kind: 'duration' | 'money' | 'count') => {
    if (value === null) return '—';
    if (kind === 'duration') return formatDuration(value);
    if (kind === 'money') return formatEtb(value);
    return `${value} payments`;
  };

  const approvalDelta = deltaText(
    current?.requestToApprovalHours ?? null,
    previous?.requestToApprovalHours ?? null,
  );
  const paidDelta = deltaText(current?.approvalToPaidHours ?? null, previous?.approvalToPaidHours ?? null);
  const avgDelta = deltaText(current?.requestToPaidAvgHours ?? null, previous?.requestToPaidAvgHours ?? null);

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Payment Lifecycle — Speed to Paid</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Request → final approval → paid, measured on payments settled in the selected range.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label htmlFor="from" className="text-xs text-muted-foreground">
                From
              </Label>
              <Input
                id="from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
              />
            </div>
            <div>
              <Label htmlFor="to" className="text-xs text-muted-foreground">
                To
              </Label>
              <Input
                id="to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
              />
            </div>
            <Badge variant="secondary" className="mb-1">
              {isLoading ? 'Loading…' : `${current?.paidCount ?? 0} paid`}
            </Badge>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Request → approval"
            value={formatDuration(current?.requestToApprovalHours ?? null)}
            sub={approvalDelta?.label}
            tone={approvalDelta ? (approvalDelta.faster ? 'good' : 'bad') : 'neutral'}
          />
          <KpiCard
            label="Approval → paid"
            value={formatDuration(current?.approvalToPaidHours ?? null)}
            sub={paidDelta?.label}
            tone={paidDelta ? (paidDelta.faster ? 'good' : 'bad') : 'neutral'}
          />
          <KpiCard
            label="Request → paid avg"
            value={formatDuration(current?.requestToPaidAvgHours ?? null)}
            sub={avgDelta?.label}
            tone={avgDelta ? (avgDelta.faster ? 'good' : 'bad') : 'neutral'}
          />
          <KpiCard
            label="Request → paid median"
            value={formatDuration(current?.requestToPaidMedianHours ?? null)}
            sub={`${current?.paidCount ?? 0} paid payments in range`}
            tone="neutral"
          />
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Lifecycle stage
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Previous period {data ? `· ${data.previousLabel}` : ''}
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Selected range {data ? `· ${data.currentLabel}` : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-b last:border-0">
                    <td className="px-6 py-3.5 text-foreground">{r.label}</td>
                    <td className="px-6 py-3.5 text-right text-muted-foreground">
                      {renderCell(r.previous, r.kind)}
                    </td>
                    <td className={`px-6 py-3.5 text-right font-medium ${rowTone(r.current, r.previous)}`}>
                      {renderCell(r.current, r.kind)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-6 py-3.5 text-foreground">Control target</td>
                  <td className="px-6 py-3.5 text-right text-muted-foreground">—</td>
                  <td className="px-6 py-3.5 text-right text-muted-foreground">
                    Pay ≤24h · escalate &gt;48h
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
